/**
 * Cookie consent helpers (Cookiebot CMP) for first-party code.
 *
 * The Cookiebot WordPress plugin prints the Google Consent Mode defaults, GTM
 * and Cookiebot's uc.js (automatic blocking) in <head>. Automatic blocking only
 * holds back scripts Cookiebot has classified; it does not stop our own code
 * from using localStorage/sessionStorage/cookies. Any theme code that reads or
 * writes non-essential browser storage must ask these helpers first.
 *
 * Public Cookiebot JS API used (https://www.cookiebot.com/en/developer/):
 *   Cookiebot.consent.{necessary,preferences,statistics,marketing,method}
 *   Cookiebot.{hasResponse,consented,declined,renew()}
 *   window events CookiebotOnLoad / OnConsentReady / OnAccept / OnDecline
 *
 * Everything here is safe when Cookiebot is missing (local dev, a blocked
 * uc.js): no CMP means no consent for anything except strictly necessary
 * storage.
 */

export const CATEGORIES = ['necessary', 'preferences', 'statistics', 'marketing'];

const EVENTS = ['CookiebotOnLoad', 'CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'];

// How long after window load before treating the page as having no CMP.
// Scripts in the HTML or inserted before load (an async uc.js, or one GTM
// injects) delay the load event until they have run or failed, so by then
// window.Cookiebot either exists or is not coming.
const ABSENT_GRACE_MS = 1000;

function cmp() {
    const cb = window.Cookiebot;
    return cb && typeof cb === 'object' && cb.consent ? cb : null;
}

/**
 * 'absent'    — no CMP on the page (yet).
 * 'undecided' — the banner is waiting for the visitor's answer.
 * 'decided'   — consent is known: answered now or on an earlier visit
 *               (hasResponse), or applied by the CMP without an answer
 *               (opt-out regions / regions configured without a banner).
 */
export function cmpStatus() {
    const cb = cmp();
    if (!cb) return 'absent';
    if (cb.hasResponse || cb.consented || cb.declined || cb.consent.method) return 'decided';
    return 'undecided';
}

/** True when storage in this Cookiebot category is allowed right now. */
export function hasConsent(category) {
    if (category === 'necessary') return true;
    const cb = cmp();
    return !!cb && cmpStatus() === 'decided' && cb.consent[category] === true;
}

/** Calls fn on every Cookiebot consent event: load, answer, change, withdrawal. */
export function onConsentChange(fn) {
    EVENTS.forEach((name) => window.addEventListener(name, fn));
}

/**
 * Calls fn(status) once, as soon as consent is known: immediately when it
 * already is, otherwise when the visitor answers the banner. With no CMP
 * (missing, blocked or failed uc.js) it settles with 'absent' shortly after
 * window load. A banner left unanswered never settles — callers must not act
 * on consent they don't have. A CMP that only appears later still reaches
 * onConsentChange() listeners.
 */
export function whenConsentKnown(fn) {
    if (cmpStatus() === 'decided') {
        fn('decided');
        return;
    }

    let done = false;
    let timer = 0;

    function finish(status) {
        done = true;
        window.clearTimeout(timer);
        EVENTS.forEach((name) => window.removeEventListener(name, check));
        fn(status);
    }

    function check() {
        if (!done && cmpStatus() === 'decided') finish('decided');
    }

    function checkAbsent() {
        timer = window.setTimeout(() => {
            if (!done && cmpStatus() === 'absent') finish('absent');
        }, ABSENT_GRACE_MS);
    }

    EVENTS.forEach((name) => window.addEventListener(name, check));

    if (document.readyState === 'complete') checkAbsent();
    else window.addEventListener('load', checkAbsent, { once: true });
}

/**
 * "Cookie settings" links. Any <a href="#cookie-settings"> (for example a
 * Custom Link added to a footer menu in Appearance › Menus) or element with
 * [data-cookie-settings] reopens the Cookiebot dialog so visitors can review,
 * change or withdraw consent. Menus strip javascript: URLs, hence the fragment
 * hook. Without a CMP the click is left alone.
 */
export function initCookieSettingsLinks() {
    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('a[href$="#cookie-settings"], [data-cookie-settings]');
        if (!trigger) return;
        const cb = cmp();
        if (!cb || typeof cb.renew !== 'function') return;
        e.preventDefault();
        cb.renew();
    });
}
